import type { Rgb } from '@/types/engine';
import type { BlendMode } from '@/state/appState';

export interface StringStyle {
  color: Rgb;
  opacity: number;
  thickness: number;
  background: Rgb;
  blendMode: BlendMode;
  roughness: number;
  brightness: number;
  transparentBackground?: boolean;
}

export interface ViewTransform {
  scale: number;
  offsetX: number;
  offsetY: number;
}

export function rgbCss([r, g, b]: Rgb, alpha = 1): string {
  return alpha >= 1 ? `rgb(${r | 0},${g | 0},${b | 0})` : `rgba(${r | 0},${g | 0},${b | 0},${alpha})`;
}

export function hash01(i: number): number {
  let x = (i + 0x9e3779b9) | 0;
  x = Math.imul(x ^ (x >>> 16), 0x85ebca6b);
  x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35);
  x ^= x >>> 16;
  return (x >>> 0) / 4294967296;
}

type AnyCanvas = HTMLCanvasElement | OffscreenCanvas;
type AnyContext = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

function makeCanvas(width: number, height: number): AnyCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  return c;
}

interface Checkpoint {
  count: number;
  canvas: AnyCanvas;
}

export class CanvasRenderer {
  private readonly ctx: AnyContext;
  private width = 0;
  private height = 0;
  private dpr = 1;
  private targetWidth = 1;
  private targetHeight = 1;
  private transform: ViewTransform = { scale: 1, offsetX: 0, offsetY: 0 };
  private pins: Float32Array = new Float32Array(0);
  private sequence: Uint16Array = new Uint16Array(0);
  private style: StringStyle = {
    color: [20, 20, 24],
    opacity: 0.25,
    thickness: 1,
    background: [250, 249, 246],
    blendMode: 'source-over',
    roughness: 0,
    brightness: 0,
  };
  private drawnCount = -1;
  private epoch = 0;
  private drawnEpoch = -1;
  private checkpoints: Checkpoint[] = [];
  private checkpointInterval = 500;
  private maxCheckpoints = 24;

  constructor(private readonly canvas: AnyCanvas) {
    const ctx = canvas.getContext('2d') as AnyContext | null;
    if (!ctx) throw new Error('Canvas 2D context is not available.');
    this.ctx = ctx;
  }

  get element(): AnyCanvas {
    return this.canvas;
  }

  /** Sets the backing store size in CSS pixels and device pixel ratio. */
  setViewport(width: number, height: number, dpr: number): void {
    const w = Math.max(1, Math.round(width * dpr));
    const h = Math.max(1, Math.round(height * dpr));
    if (w === this.canvas.width && h === this.canvas.height && dpr === this.dpr) return;
    this.canvas.width = w;
    this.canvas.height = h;
    this.width = width;
    this.height = height;
    this.dpr = dpr;
    this.invalidate();
  }

  setTargetSize(width: number, height: number): void {
    if (width === this.targetWidth && height === this.targetHeight) return;
    this.targetWidth = Math.max(1, width);
    this.targetHeight = Math.max(1, height);
    this.invalidate();
  }

  setTransform(t: ViewTransform): void {
    if (t.scale === this.transform.scale && t.offsetX === this.transform.offsetX && t.offsetY === this.transform.offsetY) return;
    this.transform = { ...t };
    this.invalidate();
  }

  getTransform(): ViewTransform {
    return { ...this.transform };
  }

  setPins(pins: Float32Array): void {
    if (pins === this.pins) return;
    this.pins = pins;
    this.invalidate();
  }

  setStyle(style: StringStyle): void {
    const s = this.style;
    if (
      s.color[0] === style.color[0] && s.color[1] === style.color[1] && s.color[2] === style.color[2] &&
      s.opacity === style.opacity && s.thickness === style.thickness &&
      s.background[0] === style.background[0] && s.background[1] === style.background[1] && s.background[2] === style.background[2] &&
      s.blendMode === style.blendMode && s.roughness === style.roughness && s.brightness === style.brightness &&
      (s.transparentBackground ?? false) === (style.transparentBackground ?? false)
    ) {
      return;
    }
    this.style = { ...style, color: [...style.color] as Rgb, background: [...style.background] as Rgb };
    this.invalidate();
  }

  setSequence(sequence: Uint16Array): void {
    if (sequence === this.sequence) return;
    const sharesPrefix = sequence.buffer === this.sequence.buffer && sequence.byteOffset === this.sequence.byteOffset;
    this.sequence = sequence;
    if (!sharesPrefix) this.invalidate();
    else if (this.drawnCount * 2 > sequence.length) this.invalidate();
  }

  invalidate(): void {
    this.epoch++;
    this.checkpoints = [];
    this.drawnCount = -1;
  }

  get stringCount(): number {
    return this.sequence.length >>> 1;
  }

  toCanvas(x: number, y: number): [number, number] {
    const { scale, offsetX, offsetY } = this.transform;
    return [x * scale + offsetX, y * scale + offsetY];
  }

  draw(upTo: number): number {
    const total = this.stringCount;
    const count = Math.max(0, Math.min(total, Math.floor(upTo)));
    if (this.drawnEpoch === this.epoch && this.drawnCount === count) return count;

    const ctx = this.ctx;
    if (this.drawnEpoch !== this.epoch || this.drawnCount < 0 || count < this.drawnCount) {
      // Start from the nearest checkpoint below `count`, or from scratch.
      const cp = this.drawnEpoch === this.epoch ? this.bestCheckpoint(count) : null;
      if (cp) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalCompositeOperation = 'copy';
        ctx.globalAlpha = 1;
        ctx.drawImage(cp.canvas, 0, 0);
        ctx.globalCompositeOperation = 'source-over';
        this.drawnCount = cp.count;
      } else {
        this.drawBackground();
        this.drawnCount = 0;
        if (this.drawnEpoch !== this.epoch) this.checkpoints = [];
      }
      this.drawnEpoch = this.epoch;
    }
    this.checkpointInterval = Math.max(250, Math.ceil(total / 16));
    this.drawStrings(this.drawnCount, count);
    this.drawnCount = count;
    return count;
  }

  private bestCheckpoint(count: number): Checkpoint | null {
    let best: Checkpoint | null = null;
    for (const cp of this.checkpoints) {
      if (cp.count <= count && (!best || cp.count > best.count)) best = cp;
    }
    return best;
  }

  private drawBackground(): void {
    const ctx = this.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    if (!this.style.transparentBackground) {
      // Only the artboard is paper; the desk around it stays visible on screen.
      // Exports size their canvas to the artboard, so this still fills them edge to edge.
      const { scale, offsetX, offsetY } = this.transform;
      const dpr = this.dpr;
      ctx.fillStyle = rgbCss(this.style.background);
      ctx.fillRect(offsetX * dpr, offsetY * dpr, this.targetWidth * scale * dpr, this.targetHeight * scale * dpr);
    }
  }

  private drawStrings(from: number, to: number): void {
    if (to <= from) return;
    const ctx = this.ctx;
    const { scale, offsetX, offsetY } = this.transform;
    const dpr = this.dpr;
    ctx.setTransform(dpr * scale, 0, 0, dpr * scale, dpr * offsetX, dpr * offsetY);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalCompositeOperation = this.style.blendMode;
    const thickness = Math.max(0.05, this.style.thickness) / scale;
    ctx.lineWidth = thickness;
    const b = this.style.brightness;
    const color: Rgb = [
      this.style.color[0] + (255 - this.style.color[0]) * b,
      this.style.color[1] + (255 - this.style.color[1]) * b,
      this.style.color[2] + (255 - this.style.color[2]) * b,
    ];
    ctx.strokeStyle = rgbCss(color);
    const pins = this.pins;
    const seq = this.sequence;
    const pinCount = pins.length >>> 1;
    const baseAlpha = this.style.opacity;
    const roughness = this.style.roughness;
    for (let i = from; i < to; i++) {
      const a = seq[i * 2];
      const c = seq[i * 2 + 1];
      if (a >= pinCount || c >= pinCount) continue;
      ctx.globalAlpha = roughness > 0 ? baseAlpha * (1 - roughness * 0.6 * hash01(i)) : baseAlpha;
      ctx.beginPath();
      ctx.moveTo(pins[a * 2], pins[a * 2 + 1]);
      ctx.lineTo(pins[c * 2], pins[c * 2 + 1]);
      ctx.stroke();
      const next = i + 1;
      if (next % this.checkpointInterval === 0 && next < this.stringCount) this.storeCheckpoint(next);
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  private storeCheckpoint(count: number): void {
    if (this.checkpoints.some((c) => c.count === count)) return;
    if (this.checkpoints.length >= this.maxCheckpoints) {
      // Drop the checkpoint that leaves the most even coverage (the second one).
      this.checkpoints.splice(1, 1);
    }
    const snap = makeCanvas(this.canvas.width, this.canvas.height);
    const sctx = snap.getContext('2d') as AnyContext | null;
    if (!sctx) return;
    sctx.drawImage(this.canvas, 0, 0);
    this.checkpoints.push({ count, canvas: snap });
  }

  /** Copies the current canvas into another context (used for exports). */
  blitTo(ctx: AnyContext, dx = 0, dy = 0, dw?: number, dh?: number): void {
    ctx.drawImage(this.canvas, dx, dy, dw ?? this.canvas.width, dh ?? this.canvas.height);
  }
}
